import React, { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
// hooks
import useDialog from "hooks/useDialog";
import { useAppDispatch } from "hooks/redux";
// Async
import PropertiesAsync from "store/properties/propertiesAsync";
import ReservationRequestsAsync from "store/reservationRequests/reservationRequestsAsync";
// Actions
import { reservationRequestsActions } from "store/reservationRequests/reservationRequestsSlice";
import { PropertiesActions } from "store/properties/propertiesSlice";
// Models
import IProperty from "models/Property";
// Types
import UserRoles from "types/UserRoles";
// Selectors
import { selectIsAuthenticated } from "store/auth/authSelectors";
import { selectAllProperties } from "store/properties/propertiesSelectors";
import { selectParams, selectPropertyOwnershipDetails } from "store/propertyOwnerships/propertyOwnershipsSelectors";
import { selectCurrentUser } from "store/users/usersSelectors";
// components
import SignInForm from "./SignIn.form";
import Title from "./Title";
import { Input } from "./Controls";
import Phone, { getCallingCode, getPhoneWithoutCallingCode } from "./Phone";
import AddGuestCapabilities from "./AddGuestCapabilities";
// mui
import { LoadingButton, MobileDatePicker } from "@mui/lab";
import {
  Autocomplete, Box, Button, Checkbox,
  debounce, Grid, Paper, TextField, Typography
} from "@mui/material";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// Utilities
import { isEmailValid, isFieldRequired } from "utilities/Validation";
import { getContent } from 'utilities/Utilities';

type Props = {
  onClose?: () => void,
}

interface IForm {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  callingCode?: string;
  role?: UserRoles;
  notificationsEnabled?: boolean;

  propertyId?: any;
  start: any;
  end: any;
  guestNotes: string;
};

const ReservationRequestForm:React.FC<Props> = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const currentUser = useSelector(selectCurrentUser);
  const propertyOwnership = useSelector(selectPropertyOwnershipDetails);
  const propertyOwnershipsParams = useSelector(selectParams);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const properties = useSelector(selectAllProperties) || [];

  const [accepted, setAccepted] = useState<boolean>(isAuthenticated ? true : false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingProperties, setIsLoadingProperties] = useState<boolean>(false);

  useEffect(() => { if(isAuthenticated) setAccepted(true)}, [isAuthenticated]);

  const { control, handleSubmit, formState:{ errors }, setValue, watch } = useForm<IForm>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      callingCode: '',
      phone: '',
      role: UserRoles.Guest,
      notificationsEnabled: true,

      propertyId: propertyOwnership?.property || propertyOwnershipsParams.propertyId || null,
      start: propertyOwnershipsParams?.start || propertyOwnership?.start || dayjs(),
      end: propertyOwnershipsParams?.end || propertyOwnership?.end || dayjs().add(7, 'days'),
      guestNotes: '',
    }
  });

  const startDate = watch('start');

  const onSubmit = handleSubmit((data: IForm) => {
    setIsLoading(true);
    const { firstName, lastName, email, phone, callingCode, role, notificationsEnabled, propertyId, ...reservationRequestData } = data;

    if (isAuthenticated) {
      const nextData: IForm = {
        ...reservationRequestData,
        start: dayjs(reservationRequestData.start).format('YYYY-MM-DD'),
        end: dayjs(reservationRequestData.end).format('YYYY-MM-DD'),
      }
      if (propertyId) nextData['propertyId'] = propertyId.id;

      dispatch(ReservationRequestsAsync.createReservationRequest(nextData))
        .unwrap()
        .then(() => {
          if (onClose) onClose();
          navigate('/submitted-request');
        })
        .finally(() => {
          setIsLoading(false);
        })
    } else {
      const nextData: any = {
        registrationRequestCreateData: {
          accountId: getContent('settings').accountId,
          firstName, lastName, email, role, notificationsEnabled,
          callingCode: callingCode ? getCallingCode(callingCode) : null,
          phone: phone && callingCode ? getPhoneWithoutCallingCode(callingCode, phone) : null,
        },
        reservationRequestCreateData: {
          ...reservationRequestData,
          start: dayjs(reservationRequestData.start).format('YYYY-MM-DD'),
          end: dayjs(reservationRequestData.end).format('YYYY-MM-DD'),
        }
      }

      if (propertyId) nextData.reservationRequestCreateData['propertyId'] = propertyId.id;

      dispatch(ReservationRequestsAsync.createReservationRequestAnonymous(nextData))
        .unwrap()
        .then(() => {
          dispatch(reservationRequestsActions.setIsCreatedUser(true));
        })
        .then(() => {
          if (onClose) onClose();
          navigate('/submitted-request');
        })
        .finally(() => {
          setIsLoading(false);
        })
    }
  })

  // eslint-disable-next-line
  const debounceProperty = useCallback(debounce((search:string) => {
    if (search.trim()) {
      setIsLoadingProperties(true);
      dispatch(PropertiesAsync.fetchProperties({ search, size: 50 }))
        .unwrap()
        .finally(() => setIsLoadingProperties(false));
    } else {
      dispatch(PropertiesActions.clearProperties());
    }
  }, 500), []);

  const onChangeProperty = (event:React.ChangeEvent<HTMLInputElement>) => {
    debounceProperty(event.target.value);
  }

  const { Dialog, openDialog, closeDialog } = useDialog();
  const { Dialog: GuestCapabilitiesDialog, openDialog: openGuestCapabilitiesDialog, closeDialog: closeGuestCapabilitiesDialog } = useDialog();

  return (
    <React.Fragment>
      <Dialog maxWidth="sm">
        <SignInForm onClose={closeDialog} requestQuote={true} />
      </Dialog>
      <GuestCapabilitiesDialog maxWidth="sm">
        <AddGuestCapabilities onClose={closeGuestCapabilitiesDialog} />
      </GuestCapabilitiesDialog>

      <Box>
        {pathname !== '/timeshares' && !onClose && (
          <Box sx={{ pb: 2 }}>
            <Title>Can't find what you are looking for?</Title>
          </Box>
        )}
        <Paper sx={{ boxShadow: '0px 4px 32px rgba(0, 0, 0, 0.08)', borderRadius: '8px', p: { xs: 2, sm: 3 } }}>
          {pathname === '/timeshares' && !onClose && (
            <Box sx={{ pb: 2 }}>
              <Title>Can't find what you are looking for?</Title>
            </Box>
          )}
          <form onSubmit={onSubmit} noValidate>
            {!isAuthenticated && (
              <React.Fragment>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, backgroundColor: `rgba(${getContent('theme').primaryColorRgb}, 0.12)`, p: 2, flexDirection: { xs: 'column', md: 'row' }  }}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <InfoOutlinedIcon sx={{ color: getContent('theme').primaryColor }} />
                    <Typography sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>By sending a reservation request, a new account will be created</Typography>
                  </Box>
                  <Button
                    size="small"
                    onClick={openDialog}
                  >
                    Already registered? Sign in
                  </Button>
                </Box>
                <Box sx={{ pt: 3 }}>
                  <Typography>Contact details</Typography>
                  <Grid container spacing={2}>
                    {/* First Name */}
                    <Grid item xs={12} sm={6}>
                      <Controller
                        control={control} name="firstName"
                        rules={{ required: isFieldRequired }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            label="First name"
                            required
                            error={Boolean(errors.firstName)}
                            helperText={errors.firstName ? errors.firstName.message : ''}
                          />
                        )}
                      />
                    </Grid>
                    {/* Last Name */}
                    <Grid item xs={12} sm={6}>
                      <Controller
                        control={control} name="lastName"
                        rules={{ required: isFieldRequired }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            label="Last name"
                            required
                            error={Boolean(errors.lastName)}
                            helperText={errors.lastName ? errors.lastName.message : ''}
                          />
                        )}
                      />
                    </Grid>

                    {/* E-mail */}
                    <Grid item xs={12} sm={6}>
                      <Controller
                        control={control} name="email"
                        rules={{ required: isFieldRequired, pattern: isEmailValid }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            label="E-mail" type="email"
                            required
                            error={Boolean(errors.email)}
                            helperText={errors.email ? errors.email.message : ''}
                          />
                        )}
                      />
                    </Grid>

                    {/* Phone */}
                    <Grid item xs={12} sm={6}>
                      <Controller
                        control={control} name="phone"
                        render={({ field:{ value, onChange } }) => (
                          <Phone
                            label="Cell phone" value={value || ''}
                            onChange={([ callingCode, phone ]:string[]) => {
                              setValue('callingCode', callingCode);
                              onChange(phone);
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </React.Fragment>
            )}
            <Box sx={{ pt: 3 }}>
              <Typography>Request details</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    control={control} name="propertyId"
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        sx={{ backgroundColor: '#fff' }}
                        fullWidth
                        disablePortal
                        id="reservation-request-select-propertyId"
                        options={properties}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(property: IProperty) => property.name}
                        value={value || null}
                        onChange={(_:any, value:IProperty | null) => onChange(value || null)}
                        onClose={() => debounceProperty('')}
                        loading={isLoadingProperties}
                        loadingText="Search..."
                        noOptionsText=""
                        forcePopupIcon={false}
                        filterOptions={options => options}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            {option.name}
                          </li>
                        )}
                        renderInput={(params) => 
                          <TextField
                            {...params}
                            margin="normal"
                            label="Property"
                            onChange={onChangeProperty}
                            placeholder="e.g. Marriott"
                            helperText="Don’t see what you’re looking for? Submit your desired destination in the notes and an agent will contact you."
                          />
                        }
                      />
                    )}
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      {/* Start date */}
                      <Controller
                        control={control} name="start"
                        rules={{ required: isFieldRequired }}
                        render={({ field: { onChange, value } }) => (
                          <MobileDatePicker
                            value={value || null}
                            onChange={(date:any) => {
                              onChange(date);
                              setValue('end', dayjs(date).add(7, 'day'));
                            }}
                            label="Start date"
                            renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      {/* End date */}
                      <Controller
                        control={control} name="end"
                        rules={{ required: isFieldRequired }}
                        render={({ field }) => (
                          <MobileDatePicker
                            {...field}
                            minDate={dayjs(startDate).add(1, 'day')}
                            label="End date"
                            renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    {/* guestNotes */}
                    <Controller
                      control={control} name="guestNotes"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          margin="normal"
                          label="Notes"
                          type="text"
                          multiline
                          rows={3}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Box>
            {!isAuthenticated && (
              <React.Fragment>
                <label style={{ display: 'flex', alignItems: 'center', paddingTop: '8px' }}>
                  <Checkbox checked={accepted} onChange={(_:any, checked: boolean) => setAccepted(checked)} />
                  <Typography sx={{ fontSize: '14px' }}>
                    {'* I have read and accepted the '}
                    <NavLink to="/terms-and-conditions" target="_blank" style={{ color: getContent('theme').primaryColor, textDecoration: 'none' }}>Terms & Conditions</NavLink>
                    {' and '}
                    <NavLink to="/privacy-policy" target="_blank" style={{ color: getContent('theme').primaryColor, textDecoration: 'none' }}>Privacy Policy</NavLink>
                  </Typography>
                </label>
              </React.Fragment>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2, gap: 2 }}>
              {onClose && (
                <Button
                  variant="outlined"
                  onClick={onClose}
                >
                  Cancel
                </Button>
              )}
              <LoadingButton
                loading={isLoading}
                variant="contained"
                disabled={!accepted}
                onClick={() => {
                  if(currentUser && !currentUser?.roles.includes(UserRoles.Guest)) openGuestCapabilitiesDialog();
                }}
                type={ !currentUser || currentUser?.roles.includes(UserRoles.Guest) ? 'submit' : 'button'}
              >
                Send reservation request
              </LoadingButton>
            </Box>
          </form>
        </Paper>
      </Box>
    </React.Fragment>
  )
}

export default ReservationRequestForm;
